/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Save, CheckCircle2, Sun, Moon, Coffee } from 'lucide-react';

const BLOCK_ICON = {
  MORNING_OUTREACH:   <Sun className="w-3.5 h-3.5" />,
  AFTERNOON_GROUPS:   <Coffee className="w-3.5 h-3.5" />,
  EVENING_ONE_TO_ONE: <Moon className="w-3.5 h-3.5" />,
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function DailyExecutionSheetPage() {
  const [blocks, setBlocks] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [bR, sR] = await Promise.all([
        axios.get(`${API}/terra-growth/reference/execution-blocks`),
        axios.get(`${API}/terra-growth/execution/today`),
      ]);
      setBlocks(bR.data?.blocks || []);
      setSheet(sR.data || { entries: [] });
    } catch (e) { toast.error('Could not load execution sheet.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const setEntry = (idx, patch) => {
    setSheet(s => ({
      ...s,
      entries: s.entries.map((e, i) => i === idx ? { ...e, ...patch } : e),
    }));
  };

  const addEntry = (block_code) => {
    setSheet(s => ({ ...s, entries: [...(s.entries || []), {
      block_code, title: '', completed: false, movement_note: '',
    }] }));
  };

  const removeEntry = (idx) => {
    setSheet(s => ({ ...s, entries: s.entries.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = (sheet.entries || []).filter(e => e.title && e.title.trim());
      const r = await axios.post(`${API}/terra-growth/execution/today`, {
        date: sheet.date || todayISO(),
        entries: cleaned.map(e => ({
          block_code: e.block_code,
          title: e.title.trim(),
          completed: !!e.completed,
          movement_note: e.movement_note?.trim() || null,
          related_lead_id: e.related_lead_id || null,
          related_event_id: e.related_event_id || null,
        })),
      });
      setSheet(r.data);
      toast.success(`Saved · ${r.data.movement_count} movements logged.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
    finally { setBusy(false); }
  };

  const grouped = blocks.map(b => ({
    ...b,
    entries: (sheet?.entries || []).map((e, i) => ({ ...e, _idx: i }))
                                    .filter(e => e.block_code === b.code),
  }));

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="daily-execution-sheet" className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.20em] text-[#8C6239]">Terra Growth OS™ · Layer 8</p>
          <h1 className="text-3xl font-['Outfit'] font-light text-[#2C2A29] mt-1">Daily Execution Sheet</h1>
          <p className="text-sm text-[#8A8681] mt-1 max-w-xl">
            Log movement, not activity. Each entry asks: what perception shifted? Which ecosystem deepened?
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-12 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div data-testid="execution-summary" className="grid grid-cols-3 gap-3 mb-6">
              <Stat testId="execution-total" label="Entries today" value={(sheet?.entries || []).length} />
              <Stat testId="execution-completed" label="Completed" value={sheet?.completed_count || 0} />
              <Stat testId="execution-movement" label="Movement notes" value={sheet?.movement_count || 0} />
            </div>

            <div className="space-y-5">
              {grouped.map(b => (
                <section key={b.code} data-testid={`execution-block-${b.code}`}
                         className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-['Work_Sans'] text-[#2C2A29] flex items-center gap-2">
                      {BLOCK_ICON[b.code]} {b.label}
                    </h3>
                    <Button size="sm" variant="outline"
                            data-testid={`execution-add-${b.code}`}
                            onClick={() => addEntry(b.code)}
                            className="border-[#D8D4CE] text-[#5C5955] text-[11px] rounded-full px-3 h-7">
                      + add entry
                    </Button>
                  </div>
                  {b.entries.length === 0 ? (
                    <p className="text-xs text-[#8A8681]">No entries.</p>
                  ) : (
                    <ul className="space-y-2">
                      {b.entries.map(e => (
                        <li key={e._idx} data-testid={`execution-entry-${e._idx}`}
                            className="rounded-xl border border-[#EBE8E3] p-3 space-y-2">
                          <div className="flex gap-2">
                            <Input
                              data-testid={`execution-title-${e._idx}`}
                              placeholder="What did you do?"
                              value={e.title || ''}
                              onChange={(ev) => setEntry(e._idx, { title: ev.target.value })}
                              className="h-9 text-sm"
                            />
                            <button onClick={() => removeEntry(e._idx)}
                                    className="text-[#B5B0A8] hover:text-rose-600 px-2"
                                    data-testid={`execution-remove-${e._idx}`}>×</button>
                          </div>
                          <Input
                            data-testid={`execution-movement-${e._idx}`}
                            placeholder="Movement note — what shifted? (e.g. 2 leads moved CURIOUS → RESONATING)"
                            value={e.movement_note || ''}
                            onChange={(ev) => setEntry(e._idx, { movement_note: ev.target.value })}
                            className="h-9 text-sm border-[#E5D6BE] bg-[#FAF1E1]/40"
                          />
                          <label className="text-xs text-[#5C5955] inline-flex items-center gap-1">
                            <input type="checkbox" checked={!!e.completed}
                                   onChange={(ev) => setEntry(e._idx, { completed: ev.target.checked })}
                                   data-testid={`execution-completed-${e._idx}`} />
                            Completed
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            <Button data-testid="execution-save-btn" onClick={save} disabled={busy}
                    className="mt-6 bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-5">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save sheet
            </Button>
          </>
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ testId, label, value }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-4 text-center">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{label}</div>
      <div className="text-2xl font-['Outfit'] tabular-nums text-[#2C2A29] mt-1">{value}</div>
    </div>
  );
}
